import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import BadgeIcon from '@mui/icons-material/Badge';
import { api } from 'common';

/**
 * DriverRegistrationDialog - Comprehensive driver registration in one place
 * Includes personal info, vehicle details, documents, and approval
 */
export default function DriverRegistrationDialog({
  open,
  onClose,
  onSuccess,
  carTypes,
  fleetAdmin,
  settings,
  isEdit = false,
  existingDriver = null
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Personal Information
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',

    // Vehicle Information
    carType: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    vehicleNumber: '',

    // Documents
    licenseImage: '',
    licenseImageBack: '',
    verifyIdImage: '',
    verifyId: '',

    // Approval
    approved: false,
    driverActiveStatus: true,
  });

  const { addUser, checkUserExists, uploadImage } = api;

  const steps = ['Personal Info', 'Vehicle Details', 'Documents', 'Review'];

  useEffect(() => {
    if (open) {
      if (isEdit && existingDriver) {
        setFormData({
          firstName: existingDriver.firstName || '',
          lastName: existingDriver.lastName || '',
          email: existingDriver.email || '',
          mobile: existingDriver.mobile || '',
          password: '',
          confirmPassword: '',
          carType: existingDriver.carType || '',
          vehicleMake: existingDriver.vehicleMake || '',
          vehicleModel: existingDriver.vehicleModel || '',
          vehicleYear: existingDriver.vehicleYear || '',
          vehicleColor: existingDriver.vehicleColor || '',
          vehicleNumber: existingDriver.vehicleNumber || '',
          licenseImage: existingDriver.licenseImage || '',
          licenseImageBack: existingDriver.licenseImageBack || '',
          verifyIdImage: existingDriver.verifyIdImage || '',
          verifyId: existingDriver.verifyId || '',
          approved: existingDriver.approved || false,
          driverActiveStatus: existingDriver.driverActiveStatus !== false,
        });
      } else {
        // Reset for new registration
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          mobile: '',
          password: '',
          confirmPassword: '',
          carType: carTypes && carTypes.length > 0 ? carTypes[0] : '',
          vehicleMake: '',
          vehicleModel: '',
          vehicleYear: new Date().getFullYear().toString(),
          vehicleColor: '',
          vehicleNumber: '',
          licenseImage: '',
          licenseImageBack: '',
          verifyIdImage: '',
          verifyId: '',
          approved: false,
          driverActiveStatus: true,
        });
      }
      setActiveStep(0);
      setError('');
      setSuccess('');
    }
  }, [open, isEdit, existingDriver, carTypes]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleFileUpload = async (field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Convert to base64 for upload
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result;
          handleInputChange(field, base64);
          setLoading(false);
        } catch (err) {
          setError('Failed to upload image: ' + err.message);
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Upload failed: ' + err.message);
      setLoading(false);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Info
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
          setError('First name and last name are required');
          return false;
        }
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Valid email is required');
          return false;
        }
        if (!formData.mobile.trim() || formData.mobile.length < 8) {
          setError('Valid mobile number is required (min 8 digits)');
          return false;
        }
        if (!isEdit) {
          if (!formData.password || formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return false;
          }
          if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
          }
        }
        return true;

      case 1: // Vehicle Details
        if (!formData.carType) {
          setError('Please select a car type');
          return false;
        }
        if (!formData.vehicleMake.trim()) {
          setError('Vehicle make is required');
          return false;
        }
        if (!formData.vehicleModel.trim()) {
          setError('Vehicle model is required');
          return false;
        }
        if (!formData.vehicleNumber.trim()) {
          setError('License plate number is required');
          return false;
        }
        return true;

      case 2: // Documents
        if (!formData.licenseImage) {
          setError('License image (front) is required');
          return false;
        }
        if (!formData.licenseImageBack) {
          setError('License image (back) is required');
          return false;
        }
        if (settings?.imageIdApproval) {
          if (!formData.verifyIdImage) {
            setError('ID photo is required');
            return false;
          }
          if (!formData.verifyId.trim()) {
            setError('ID number is required');
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setError('');
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setLoading(true);
    setError('');

    try {
      // Check if user exists (for new registrations)
      if (!isEdit) {
        const userCheck = await checkUserExists(formData);
        if (userCheck.users && userCheck.users.length > 0) {
          setError('A user with this email or mobile already exists');
          setLoading(false);
          return;
        }
        if (userCheck.error) {
          setError('Email or mobile validation failed');
          setLoading(false);
          return;
        }
      }

      // Prepare driver data
      const driverData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobile: formData.mobile,
        carType: formData.carType,
        vehicleMake: formData.vehicleMake,
        vehicleModel: formData.vehicleModel,
        vehicleYear: formData.vehicleYear,
        vehicleColor: formData.vehicleColor,
        vehicleNumber: formData.vehicleNumber,
        licenseImage: formData.licenseImage,
        licenseImageBack: formData.licenseImageBack,
        verifyIdImage: formData.verifyIdImage,
        verifyId: formData.verifyId,
        approved: formData.approved,
        driverActiveStatus: formData.driverActiveStatus,
        usertype: 'driver',
      };

      if (!isEdit) {
        driverData.createdAt = new Date().getTime();
        // Generate referral code
        const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const reference = [...Array(5)].map(_ => c[~~(Math.random() * c.length)]).join('');
        driverData.referralId = reference;

        // Add fleet admin if applicable
        if (fleetAdmin) {
          driverData.fleetadmin = fleetAdmin;
        }
      }

      // Success
      setSuccess('Driver ' + (isEdit ? 'updated' : 'registered') + ' successfully!');
      setTimeout(() => {
        if (onSuccess) onSuccess(driverData);
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <Box>
      <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
        Enter driver's personal information
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="First Name"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          fullWidth
          label="Last Name"
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={formData.email}
        onChange={(e) => handleInputChange('email', e.target.value)}
        disabled={isEdit}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Mobile Number"
        value={formData.mobile}
        onChange={(e) => handleInputChange('mobile', e.target.value)}
        disabled={isEdit}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {!isEdit && (
        <>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}
    </Box>
  );

  const renderVehicleDetails = () => (
    <Box>
      <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
        Vehicle information
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Car Type</InputLabel>
        <Select
          value={formData.carType}
          onChange={(e) => handleInputChange('carType', e.target.value)}
          label="Car Type"
          startAdornment={
            <InputAdornment position="start">
              <DirectionsCarIcon sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          }
        >
          {carTypes && carTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Make"
          placeholder="Toyota"
          value={formData.vehicleMake}
          onChange={(e) => handleInputChange('vehicleMake', e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          fullWidth
          label="Model"
          placeholder="Camry"
          value={formData.vehicleModel}
          onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Year"
          placeholder="2023"
          value={formData.vehicleYear}
          onChange={(e) => handleInputChange('vehicleYear', e.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          fullWidth
          label="Color"
          placeholder="Black"
          value={formData.vehicleColor}
          onChange={(e) => handleInputChange('vehicleColor', e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        fullWidth
        label="License Plate Number"
        placeholder="AB12345"
        value={formData.vehicleNumber}
        onChange={(e) => handleInputChange('vehicleNumber', e.target.value.toUpperCase())}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <DriveEtaIcon sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
    </Box>
  );

  const renderDocuments = () => (
    <Box>
      <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
        Upload required documents
      </Typography>

      {/* License Front */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={500} color="#6B7280" sx={{ mb: 1 }}>
          Driver's License (Front) *
        </Typography>
        <Button
          variant="outlined"
          component="label"
          fullWidth
          startIcon={formData.licenseImage ? <CheckCircleIcon /> : <UploadFileIcon />}
          sx={{
            py: 2,
            borderStyle: 'dashed',
            borderColor: formData.licenseImage ? '#10B981' : '#D1D5DB',
            color: formData.licenseImage ? '#10B981' : '#6B7280',
            '&:hover': {
              borderColor: formData.licenseImage ? '#059669' : '#9CA3AF',
              bgcolor: 'transparent'
            }
          }}
        >
          {formData.licenseImage ? 'License Front Uploaded' : 'Upload License Front'}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => handleFileUpload('licenseImage', e)}
          />
        </Button>
        {formData.licenseImage && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <img src={formData.licenseImage} alt="License front" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }} />
          </Box>
        )}
      </Box>

      {/* License Back */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={500} color="#6B7280" sx={{ mb: 1 }}>
          Driver's License (Back) *
        </Typography>
        <Button
          variant="outlined"
          component="label"
          fullWidth
          startIcon={formData.licenseImageBack ? <CheckCircleIcon /> : <UploadFileIcon />}
          sx={{
            py: 2,
            borderStyle: 'dashed',
            borderColor: formData.licenseImageBack ? '#10B981' : '#D1D5DB',
            color: formData.licenseImageBack ? '#10B981' : '#6B7280',
            '&:hover': {
              borderColor: formData.licenseImageBack ? '#059669' : '#9CA3AF',
              bgcolor: 'transparent'
            }
          }}
        >
          {formData.licenseImageBack ? 'License Back Uploaded' : 'Upload License Back'}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => handleFileUpload('licenseImageBack', e)}
          />
        </Button>
        {formData.licenseImageBack && (
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <img src={formData.licenseImageBack} alt="License back" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }} />
          </Box>
        )}
      </Box>

      {/* ID Verification (if enabled) */}
      {settings?.imageIdApproval && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
            ID Verification (Required)
          </Typography>

          <TextField
            fullWidth
            label="ID Number"
            placeholder="Enter ID/passport number"
            value={formData.verifyId}
            onChange={(e) => handleInputChange('verifyId', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={500} color="#6B7280" sx={{ mb: 1 }}>
              ID Photo *
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={formData.verifyIdImage ? <CheckCircleIcon /> : <UploadFileIcon />}
              sx={{
                py: 2,
                borderStyle: 'dashed',
                borderColor: formData.verifyIdImage ? '#10B981' : '#D1D5DB',
                color: formData.verifyIdImage ? '#10B981' : '#6B7280',
                '&:hover': {
                  borderColor: formData.verifyIdImage ? '#059669' : '#9CA3AF',
                  bgcolor: 'transparent'
                }
              }}
            >
              {formData.verifyIdImage ? 'ID Photo Uploaded' : 'Upload ID Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleFileUpload('verifyIdImage', e)}
              />
            </Button>
            {formData.verifyIdImage && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img src={formData.verifyIdImage} alt="ID" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }} />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 3 }}>
        Review and approve driver registration
      </Typography>

      {/* Personal Info Summary */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1 }}>
          Personal Information
        </Typography>
        <Typography variant="body2" color="#6B7280">
          {formData.firstName} {formData.lastName}
        </Typography>
        <Typography variant="body2" color="#6B7280">
          {formData.email}
        </Typography>
        <Typography variant="body2" color="#6B7280">
          {formData.mobile}
        </Typography>
      </Box>

      {/* Vehicle Info Summary */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1 }}>
          Vehicle Information
        </Typography>
        <Typography variant="body2" color="#6B7280">
          {formData.vehicleYear} {formData.vehicleMake} {formData.vehicleModel}
        </Typography>
        <Typography variant="body2" color="#6B7280">
          Color: {formData.vehicleColor || 'Not specified'}
        </Typography>
        <Typography variant="body2" color="#6B7280">
          License Plate: {formData.vehicleNumber}
        </Typography>
        <Chip label={formData.carType} size="small" sx={{ mt: 1 }} />
      </Box>

      {/* Documents Summary */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1 }}>
          Documents
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="License Front"
            size="small"
            color={formData.licenseImage ? 'success' : 'default'}
            icon={formData.licenseImage ? <CheckCircleIcon /> : undefined}
          />
          <Chip
            label="License Back"
            size="small"
            color={formData.licenseImageBack ? 'success' : 'default'}
            icon={formData.licenseImageBack ? <CheckCircleIcon /> : undefined}
          />
          {settings?.imageIdApproval && (
            <>
              <Chip
                label="ID Photo"
                size="small"
                color={formData.verifyIdImage ? 'success' : 'default'}
                icon={formData.verifyIdImage ? <CheckCircleIcon /> : undefined}
              />
              <Chip
                label={`ID: ${formData.verifyId || 'Not provided'}`}
                size="small"
              />
            </>
          )}
        </Box>
      </Box>

      {/* Admin Controls */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
        Admin Controls
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={formData.approved}
            onChange={(e) => handleInputChange('approved', e.target.checked)}
            color="success"
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Approve Driver
            </Typography>
            <Typography variant="caption" color="#6B7280">
              Driver will be able to accept rides
            </Typography>
          </Box>
        }
        sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.driverActiveStatus}
            onChange={(e) => handleInputChange('driverActiveStatus', e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Set as Active
            </Typography>
            <Typography variant="caption" color="#6B7280">
              Driver status will be active
            </Typography>
          </Box>
        }
        sx={{ display: 'flex', alignItems: 'flex-start' }}
      />
    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderVehicleDetails();
      case 2:
        return renderDocuments();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DirectionsCarIcon />
          <Typography variant="h6" fontWeight={600}>
            {isEdit ? 'Edit Driver' : 'Register New Driver'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <DialogContent sx={{ mt: 3, minHeight: 400 }}>
        {getStepContent(activeStep)}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#6B7280',
            '&:hover': { bgcolor: '#F3F4F6' }
          }}
        >
          Cancel
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#374151',
                '&:hover': { bgcolor: '#F3F4F6' }
              }}
            >
              Back
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={loading}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)'
                }
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : (isEdit ? 'Update Driver' : 'Register Driver')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
